/**
 * ApplyFilterModal — applies selected Step-4 root cause results as a global
 * object attribute filter (with Include/Exclude mode and Any/All matching).
 *
 * Pre-filled with the selected rows' attribute=value pairs, merged with the
 * currently active global object attribute filters so the modal always shows
 * the full resulting filter group. Applying replaces that group and takes
 * effect immediately (all dashboard components refetch).
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
} from '../../../../components/ui/dialog';
import { Button } from '../../../../components/ui/button';
import { useGlobalFilters } from '../../../../stores/GlobalFilterContext/GlobalFilterContext';

const conditionKey = (c) => `${c.attribute} ${c.value}`;

const ApplyFilterModal = ({ open, onOpenChange, selectedConditions }) => {
  const { filterState, dispatch } = useGlobalFilters();

  const [conditions, setConditions] = useState([]);
  const [mode, setMode] = useState('include');
  const [matchType, setMatchType] = useState('any');

  // Snapshot on open: selected rows merged with currently active filters, deduped by attribute+value
  useEffect(() => {
    if (!open) return;

    const merged = [];
    const seen = new Set();

    (selectedConditions || []).forEach((c) => {
      const key = conditionKey(c);
      if (seen.has(key)) return;
      seen.add(key);
      merged.push({ objectType: c.objectType, attribute: c.attribute, value: c.value });
    });

    const activeFilters = (filterState.objectAttributeFilters || []).filter((f) => f.enabled);
    activeFilters.forEach((f) => {
      const key = conditionKey(f);
      if (seen.has(key)) return;
      seen.add(key);
      merged.push({ objectType: null, attribute: f.attribute, value: f.value });
    });

    setConditions(merged);

    // Start from the active group settings when extending an existing filter,
    // otherwise default to include/any (typical bulk case: several values of one attribute)
    if (activeFilters.length > 0) {
      const settings = filterState.objectAttributeFilterSettings || {};
      setMode(settings.mode || 'include');
      setMatchType(settings.matchType || 'all');
    } else {
      setMode('include');
      setMatchType('any');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const removeCondition = (index) => {
    setConditions((prev) => prev.filter((_, i) => i !== index));
  };

  // 'All' with two different values of the same attribute can never match an object
  const hasSameAttributeConflict = useMemo(() => {
    if (matchType !== 'all') return false;
    const attrs = conditions.map((c) => c.attribute);
    return new Set(attrs).size < attrs.length;
  }, [conditions, matchType]);

  const handleApply = () => {
    dispatch({
      type: 'APPLY_OBJECT_ATTRIBUTE_FILTER_CONFIG',
      payload: {
        conditions: conditions.map(({ attribute, value }) => ({ attribute, value })),
        mode,
        matchType,
      },
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Apply Selection as Global Filter</DialogTitle>
        </DialogHeader>

        <div className="p-6 space-y-4 overflow-y-auto flex-grow">
          {/* Conditions */}
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">
              Filter Conditions ({conditions.length})
            </p>
            {conditions.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {conditions.map((cond, index) => (
                  <span
                    key={`${cond.attribute}-${cond.value}-${index}`}
                    className="inline-flex items-center gap-1.5 px-2 py-1 text-xs bg-blue-50 border border-blue-200 text-blue-800 rounded"
                    title={`${cond.attribute} = ${cond.value}`}
                  >
                    {cond.objectType && <span className="text-blue-500">{cond.objectType}:</span>}
                    <span className="font-medium">{cond.attribute}</span>
                    <span>=</span>
                    <span className="max-w-[200px] truncate">{cond.value}</span>
                    <button
                      onClick={() => removeCondition(index)}
                      className="ml-0.5 text-blue-400 hover:text-red-600"
                      title="Remove condition"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-500 italic">
                No conditions left — add rows in the results table or cancel.
              </p>
            )}
          </div>

          {/* Mode radio */}
          <div className="flex items-center gap-4 p-2 bg-gray-50 rounded-md">
            <span className="text-xs font-medium text-gray-700">Mode:</span>
            {['include', 'exclude'].map((m) => (
              <label key={m} className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="rca-apply-filter-mode"
                  value={m}
                  checked={mode === m}
                  onChange={() => setMode(m)}
                  className="mr-1.5 h-3.5 w-3.5 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <span className="text-xs text-gray-700">{m === 'include' ? 'Include' : 'Exclude'}</span>
              </label>
            ))}
            <span className="text-xs text-gray-500">
              {mode === 'include' ? 'Keep matching objects' : 'Remove matching objects'}
            </span>
          </div>

          {/* Match radio */}
          <div className="flex items-center gap-4 p-2 bg-gray-50 rounded-md">
            <span className="text-xs font-medium text-gray-700">Match:</span>
            {['any', 'all'].map((m) => (
              <label key={m} className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="rca-apply-filter-match"
                  value={m}
                  checked={matchType === m}
                  onChange={() => setMatchType(m)}
                  className="mr-1.5 h-3.5 w-3.5 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <span className="text-xs text-gray-700">{m === 'any' ? 'Any' : 'All'}</span>
              </label>
            ))}
            <span className="text-xs text-gray-500">
              {matchType === 'any' ? 'Object matches at least one condition' : 'Object matches all conditions'}
            </span>
          </div>

          {hasSameAttributeConflict && (
            <div className="p-2 bg-yellow-50 border border-yellow-300 rounded text-xs text-yellow-800">
              ⚠️ "All" with multiple values of the same attribute can never match — an object has only
              one value per attribute. Consider switching to "Any".
            </div>
          )}

          <p className="text-xs text-gray-500">
            Applying replaces the global object attribute filter group and updates all dashboard
            components. The filter remains visible and editable in the global filter panel.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleApply} disabled={conditions.length === 0}>
            Apply Filter
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ApplyFilterModal;
